import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useEventManageContext } from './EventManagePage';
import { Heart, Star, MessageSquare, TrendingUp, Users } from 'lucide-react';
import { getFeedbackByEventId, getFeedbackStats, Feedback } from '../../services/feedback';

export default function FeedbackPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { event } = useEventManageContext();
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [stats, setStats] = useState<{
    count: number;
    averageRating: number;
    ratingDistribution: Record<number, number>;
  }>({ count: 0, averageRating: 0, ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } });

  useEffect(() => {
    const loadData = async () => {
      const id = eventId || event?.id;
      if (id) {
        // Load feedbacks and stats
        const eventFeedbacks = await getFeedbackByEventId(id);
        setFeedbacks(eventFeedbacks);
        setStats(await getFeedbackStats(id));
      }
    };
    loadData();
  }, [eventId, event?.id]);

  // Format timestamp
  const formatTime = (date: Date) => {
    return new Date(date).toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Render stars
  const renderStars = (rating: number, size: 'sm' | 'md' | 'lg' = 'md') => {
    const sizeClasses = {
      sm: 'w-4 h-4',
      md: 'w-5 h-5',
      lg: 'w-8 h-8',
    };
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${sizeClasses[size]} ${
              star <= rating
                ? 'text-yellow-400 fill-yellow-400'
                : 'text-gray-300 dark:text-gray-600'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Feedback</h2>
        <p className="text-gray-600 dark:text-gray-400">
          See what attendees think about your event
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Average Rating */}
        <div className="card p-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900/30 rounded-xl flex items-center justify-center">
              <Star className="w-6 h-6 text-yellow-500 fill-yellow-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Average Rating</p>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats.averageRating > 0 ? stats.averageRating.toFixed(1) : '-'}
                </span>
                {stats.averageRating > 0 && renderStars(Math.round(stats.averageRating), 'sm')}
              </div>
            </div>
          </div>
        </div>

        {/* Total Responses */}
        <div className="card p-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Responses</p>
              <span className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.count}
              </span>
            </div>
          </div>
        </div>

        {/* Rating Distribution */}
        <div className="card p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Rating Distribution</p>
            </div>
          </div>
          <div className="space-y-1">
            {[5, 4, 3, 2, 1].map((rating) => {
              const count = stats.ratingDistribution[rating] || 0;
              const percentage = stats.count > 0 ? (count / stats.count) * 100 : 0;
              return (
                <div key={rating} className="flex items-center gap-2 text-sm">
                  <span className="w-3 text-gray-500">{rating}</span>
                  <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                  <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-yellow-400 transition-all"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="w-8 text-right text-gray-500">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Feedback List */}
      <div className="card">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Recent Feedback
          </h3>
        </div>

        {feedbacks.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
              <Heart className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Feedback Yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Attendees can leave feedback from the event page
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {feedbacks.map((feedback) => (
              <div key={feedback.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {renderStars(feedback.rating, 'sm')}
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {formatTime(feedback.createdAt)}
                      </span>
                    </div>
                    {feedback.message && (
                      <p className="text-gray-700 dark:text-gray-300">{feedback.message}</p>
                    )}
                    {feedback.attendeeName && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                        — {feedback.attendeeName}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
